import React, { useState, useEffect } from 'react';
import { Search, Plus, ExternalLink, TrendingUp, DollarSign, Package, AlertCircle, CheckCircle, XCircle, Clock, Trash2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import authService from '../services/AuthService';
import InfoTooltip from '../components/InfoTooltip';
import MegaExport from '../components/MegaExport';
import { SUPPLIERS, getSupplierLogo, API_BASE } from '../consts';
import DealsViewer from '../components/DealsViewer';
import { getValidatedSupplierFromUrl } from '../utils';
import SupportedSuppliersModal from '../components/SupportedSuppliersModal';
import UpdateDealsOnboardingModal from '../components/UpdateDealsOnboardingModal';

const LiveScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('create'); // 'create' or 'searches'
    const [scanGroups, setScanGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [showSuppliersModal, setShowSuppliersModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const currentPageRef = React.useRef(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalGroups, setTotalGroups] = useState(0);
    const PAGE_SIZE = 7;

    // Load scan groups on mount
    useEffect(() => {
        loadScanGroups();
    }, []);

    const loadScanGroups = async (silent = false, page = currentPageRef.current) => {
        if (!silent) setLoadingGroups(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/live-scan/groups?page=${page}&page_size=${PAGE_SIZE}`
            );
            const data = await response.json();
            if (response.ok && data.groups) {
                setScanGroups(data.groups);
                setHasMore(data.has_more || false);
                setTotalGroups(data.total || 0);
            }
        } catch (error) {
            console.error('Failed to load scan groups:', error);
        } finally {
            if (!silent) setLoadingGroups(false);
        }
    };

    const scrollToDeals = () => {
        const dealsSection = document.getElementById('deals-section');
        if (dealsSection) {
            dealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Search className="text-teal-600" size={40} />
                        Live Scan
                    </h1>
                    <p className="text-gray-600">
                        Scan retail catalogs and find profitable deals across Amazon, Walmart, and eBay
                    </p>
                </div>

                <div className="flex justify-end mb-4">
                    <MegaExport scanType="live-scan" />
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => { setActiveTab('create'); setSelectedGroup(null) }}
                            className={`flex-1 py-4 px-6 font-semibold transition ${activeTab === 'create'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Plus className="inline mr-2" size={20} />
                            Create New Scan
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('searches');
                            }}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center gap-1 justify-center ${activeTab === 'searches'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Package className="inline mr-2" size={20} />
                            My Searches ({loadingGroups ? <RefreshCw className="animate-spin" size={16} /> : totalGroups})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'create' ? (
                            <CreateScanTab
                                onScanCreated={() => {
                                    loadScanGroups();
                                    setActiveTab('searches');
                                }}
                                setShowSuppliersModal={setShowSuppliersModal}
                                existingGroups={scanGroups}
                                user={user}
                            />
                        ) : (
                            <MySearchesTab
                                scanGroups={scanGroups}
                                setScanGroups={setScanGroups}
                                loadingGroups={loadingGroups}
                                onGroupSelect={(group) => {
                                    setSelectedGroup(group);
                                    setTimeout(() => scrollToDeals(), 100);
                                }}
                                onGroupDeleted={loadScanGroups}
                                onAutoUpdate={() => loadScanGroups(true)}
                                currentPage={currentPage}
                                hasMore={hasMore}
                                totalGroups={totalGroups}
                                onPageChange={(page) => {
                                    setCurrentPage(page);
                                    currentPageRef.current = page;
                                    loadScanGroups(false, page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Selected Group Details */}
                {selectedGroup && (
                    <div id="deals-section">
                        <ScanGroupDetails
                            group={selectedGroup}
                            onClose={() => setSelectedGroup(null)}
                            user={user}
                        />
                    </div>
                )}
            </div>
            <SupportedSuppliersModal
                isOpen={showSuppliersModal}
                onClose={() => setShowSuppliersModal(false)}
            />
        </div>
    );
};

// Create Scan Tab Component
const CreateScanTab = ({ onScanCreated, setShowSuppliersModal, existingGroups, user }) => {
    const [groupName, setGroupName] = useState('');
    const [urlList, setUrlList] = useState([]);
    const [currentUrl, setCurrentUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [showUniversalScanModal, setShowUniversalScanModal] = useState(false);

    const handleAddUrl = () => {
        const trimmedUrl = currentUrl.trim();

        if (!trimmedUrl) {
            alert('Please enter a URL');
            return;
        }

        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            alert('Please enter a valid URL starting with http:// or https://');
            return;
        }

        const result = getValidatedSupplierFromUrl(trimmedUrl);

        if (!result.isValid) {
            setShowUniversalScanModal(true);
            return;
        }

        // Check if URL looks like a homepage (too short/no path beyond root)
        const urlObj = new URL(trimmedUrl);
        const pathname = urlObj.pathname.replace(/\/$/, ''); // remove trailing slash
        if (pathname.length === 0) {
            alert('❌ This looks like a homepage!\n\nPlease enter a results/catalog page - one that shows products with prices and pagination.\n\nExample: https://www.target.com/s?searchTerm=cookies instead of https://www.target.com/');
            return;
        }

        const supplier = result.supplier;

        // Check for duplicates
        if (urlList.some(item => item.url === trimmedUrl)) {
            alert('This URL has already been added');
            return;
        }

        setUrlList(prev => [...prev, {
            url: trimmedUrl,
            supplier: supplier.name,
            logo: supplier.logo,
            id: Date.now()
        }]);
        setCurrentUrl('');
    };

    const handleRemoveUrl = (id) => {
        setUrlList(prev => prev.filter(item => item.id !== id));
    };

    const handleCreateScan = async () => {
        if (!groupName.trim()) {
            alert('Please enter a group name');
            return;
        }

        if (existingGroups.some(g => g.group_name.toLowerCase() === groupName.trim().toLowerCase())) {
            alert('A scan group with this name already exists. Please choose a different name.');
            return;
        }

        if (urlList.length === 0) {
            alert('Please add at least one URL');
            return;
        }

        setLoading(true);
        try {
            const supplierGroups = urlList.reduce((acc, item) => {
                if (!acc[item.supplier]) {
                    acc[item.supplier] = [];
                }
                acc[item.supplier].push(item.url);
                return acc;
            }, {});

            const response = await authService.authenticatedFetch(API_BASE + '/api/live-scan/groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    group_name: groupName,
                    suppliers: supplierGroups
                })
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    alert(data.message);
                    return;
                }
                throw new Error(data.error || 'Failed to create scan');
            }

            setGroupName('');
            setUrlList([]);
            onScanCreated();
        } catch (error) {
            alert(error.message || 'Failed to create scan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Group Name */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Scan Group Name
                </label>
                <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g., Black Friday 2024, Clearance Scan, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
            </div>

            {/* URL Input */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Add Catalog URLs
                </label>

                {/* Input Field */}
                <div className="flex gap-2 mb-4">
                    <input
                        type="text"
                        value={currentUrl}
                        onChange={(e) => setCurrentUrl(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                        placeholder="https://www.academy.com/shop/clearance"
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <button
                        onClick={handleAddUrl}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-semibold flex items-center gap-2"
                    >
                        <Plus size={20} />
                        Add URL
                    </button>
                    <button
                        onClick={() => setShowSuppliersModal(true)}
                        className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold flex items-center gap-2 whitespace-nowrap"
                    >
                        <Package size={18} />
                        Suppliers
                    </button>
                </div>

                {/* URL List */}
                {urlList.length > 0 && (
                    <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                            Added URLs ({urlList.length})
                        </div>
                        {urlList.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition"
                            >
                                {item.logo && (
                                    <img
                                        src={item.logo}
                                        alt={item.supplier}
                                        className="w-8 h-8 object-contain rounded"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-gray-800">
                                        {item.supplier}
                                    </div>
                                    <div className="text-xs text-gray-600 truncate">
                                        {item.url}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRemoveUrl(item.id)}
                                    className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition"
                                    title="Remove URL"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <p className="text-xs -mt-8">
                💡 <strong>Tip:</strong> Make sure your URL is a <strong>catalog/results page</strong> with products and pagination.
                MSP will automatically scan through all available pages for you.
            </p>

            {/* Will scan for marketplaces */}
            <div className="flex items-center gap-3 flex-wrap mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm font-medium text-gray-700">Will scan for:</span>
                {(user?.marketplaces_sell_on?.length > 0) ? (
                    user.marketplaces_sell_on.map(marketplace => marketplace.toLowerCase().replace(/\s+/g, '-') !== "aip" && (
                        <img
                            key={marketplace}
                            src={`/icons/${marketplace.toLowerCase().replace(/\s+/g, '-')}.png`}
                            alt={marketplace}
                            title={marketplace}
                            className="h-6 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ))
                ) : (
                    <span className="text-sm text-gray-400">None selected</span>
                )}
                <a
                    href="/settings#marketplaces"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 px-3 py-1.5 rounded-lg transition"
                >
                    Click here to change →
                </a>
            </div>

            {/* Create Button */}
            <button
                onClick={handleCreateScan}
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-4 rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? (
                    <span className="flex items-center justify-center gap-2">
                        <RefreshCw className="animate-spin" size={20} />
                        Creating Scan...
                    </span>
                ) : (
                    <span className="flex items-center justify-center gap-2">
                        <Search size={20} />
                        Create Scan
                    </span>
                )}
            </button>

            {/* Universal Live Scan Modal */}
            {showUniversalScanModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                        <div className="bg-gradient-to-r from-teal-500 to-blue-600 p-6 text-white">
                            <h2 className="text-2xl font-bold mb-1">This supplier works on Universal Live Scan! ⭐</h2>
                            <p className="text-teal-100 text-sm">Download our Chrome Extension and scan any website instantly</p>
                        </div>
                        <div className="p-6">
                            <div className="rounded-xl overflow-hidden mb-5" style={{ aspectRatio: '16/9' }}>
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src="https://www.youtube.com/embed/rtlDOpvow3s"
                                    title="Universal Live Scan Tutorial"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                    allowFullScreen
                                    style={{ display: 'block', width: '100%', height: '100%' }}
                                />
                            </div>
                            <ul className="space-y-2 mb-6">
                                <li className="flex items-center gap-2 text-sm text-gray-700"><span className="text-teal-500 font-bold">✓</span> Scan <strong>any</strong> retail website</li>
                                <li className="flex items-center gap-2 text-sm text-gray-700"><span className="text-teal-500 font-bold">✓</span> Real-time deal discovery</li>
                                <li className="flex items-center gap-2 text-sm text-gray-700"><span className="text-teal-500 font-bold">✓</span> Faster results</li>
                            </ul>
                            <div className="flex gap-3">
                                <a
                                    href="/universal-live-scan"
                                    className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-xl font-semibold text-center hover:shadow-lg transition"
                                >
                                    Try Universal Live Scan →
                                </a>
                                <button
                                    onClick={() => setShowUniversalScanModal(false)}
                                    className="px-5 py-3 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 font-semibold transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <AlertCircle className="inline mr-2" size={16} />
                    <strong>How it works:</strong> Paste catalog page URLs and click "Add URL".
                    The system will automatically detect the supplier and extract all products to compare prices across Amazon, Walmart, and eBay.
                    You'll be notified when results are ready!
                </p>
            </div>
        </div >
    );
};

const MySearchesTab = ({ scanGroups, setScanGroups, loadingGroups, onGroupSelect, onGroupDeleted, onAutoUpdate, currentPage, hasMore, totalGroups, onPageChange }) => {
    const [deletingGroupId, setDeletingGroupId] = useState(null);
    const [rerunningGroupId, setRerunningGroupId] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(new Set());

    // Auto-refresh processing groups every 15 seconds
    useEffect(() => {
        const hasProcessingGroups = scanGroups.some(g => g.status === 'processing' || g.status === 'pending');

        if (!hasProcessingGroups) return;

        const interval = setInterval(() => {
            onAutoUpdate();
        }, 20000);

        return () => clearInterval(interval);
    }, [scanGroups]);

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Are you sure you want to delete this scan group? This will delete all associated deals.')) {
            return;
        }
        setDeletingGroupId(groupId);
        setScanGroups(prev => prev.filter(g => g.id !== groupId));
        try {
            await authService.authenticatedFetch(API_BASE + `/api/live-scan/groups/${groupId}`, {
                method: 'DELETE'
            });
            onGroupDeleted();
        } catch (error) {
            onGroupDeleted();
            alert('Failed to delete group');
        } finally {
            setDeletingGroupId(null);
        }
    };

    const handleStopGroup = async (groupId) => {
        if (!window.confirm('Stop this scan? You can resume it later.')) {
            return;
        }

        try {
            await authService.authenticatedFetch(API_BASE + `/api/live-scan/groups/${groupId}/stop`, {
                method: 'POST'
            });
            onAutoUpdate();
        } catch (error) {
            alert('Failed to stop scan');
        }
    };

    const handleResumeGroup = async (groupId) => {
        try {
            const response = await authService.authenticatedFetch(API_BASE + `/api/live-scan/groups/${groupId}/resume`, {
                method: 'POST'
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    alert(data.message);
                    return;
                }
                throw new Error(data.error || 'Failed to resume scan');
            }

            onAutoUpdate();
        } catch (error) {
            alert(error.message || 'Failed to resume scan');
        }
    };

    const handleRerunGroup = async (groupId) => {
        if (!window.confirm('Rerun this scan? All existing deals will be deleted and it will restart.')) return;
        setRerunningGroupId(groupId);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/live-scan/groups/${groupId}/rerun`,
                { method: 'POST' }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to rerun');
            }
            onAutoUpdate();
        } catch (error) {
            alert(error.message || 'Failed to rerun scan');
        } finally {
            setRerunningGroupId(null);
        }
    };

    const toggleMinimize = (groupId) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    // Add loading state display - but skip if auto-refreshing
    if (loadingGroups) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading your scans...</p>
                <p className="text-gray-500 text-sm mt-2">Please wait</p>
            </div>
        );
    }

    if (scanGroups.length === 0) {
        return (
            <div className="text-center py-12">
                <Package className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No scan groups yet</p>
                <p className="text-gray-500 text-sm">Create your first scan to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {[...scanGroups].sort((a, b) => {
                const order = { processing: 0, pending: 1, stopped: 2, completed: 3, failed: 4 };
                return (order[a.status] ?? 5) - (order[b.status] ?? 5);
            }).map(group => (
                <ScanGroupCard
                    key={group.id}
                    group={group}
                    onSelect={() => onGroupSelect(group)}
                    onDelete={() => handleDeleteGroup(group.id)}
                    onStop={() => handleStopGroup(group.id)}
                    onResume={() => handleResumeGroup(group.id)}
                    isDeleting={deletingGroupId === group.id}
                    isMinimized={!expandedGroups.has(group.id)}
                    onToggleMinimize={() => toggleMinimize(group.id)}
                    onRerun={() => handleRerunGroup(group.id)}
                    isRerunning={rerunningGroupId === group.id}
                />
            ))}
            <p className="text-xs text-gray-300 text-center" style={{ marginBottom: '-4px' }}>
                Running slow? Email contact@multisellerpro.com and we'll speed things up for you.
            </p>
            {(currentPage > 1 || hasMore) && (
                <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        ← Previous
                    </button>
                    <span className="text-sm text-black">Page {currentPage}</span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={!hasMore}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

// Scan Group Card Component - With Minimize/Expand
const ScanGroupCard = ({ group, onSelect, onDelete, onStop, onResume, isDeleting, isMinimized, onToggleMinimize, onRerun, isRerunning }) => {
    const getStatusBadge = () => {
        switch (group.status) {
            case 'completed':
                return <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <CheckCircle size={16} /> Completed
                </span>;
            case 'processing':
                return <span className="flex items-center gap-1 text-blue-700 bg-blue-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <RefreshCw className="animate-spin" size={16} /> Processing
                </span>;
            case 'pending':
                return <span className="flex items-center gap-1 text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <Clock size={16} /> Pending
                </span>;
            case 'failed':
                return <span className="flex items-center gap-1 text-red-700 bg-red-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <XCircle size={16} /> Failed
                </span>;
            case 'stopped':
                return <span className="flex items-center gap-1 text-gray-700 bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <XCircle size={16} /> Stopped
                </span>;
            default:
                return null;
        }
    };

    const groupRelatedUrls = (urls) => {
        if (!urls || urls.length === 0) return [];

        const groups = [];
        const used = new Set();

        urls.forEach((currentUrl, currentIndex) => {
            if (used.has(currentIndex)) return;

            // Start new group
            const group = {
                supplier: currentUrl.supplier,
                baseUrl: currentUrl.url,
                items: [currentUrl]
            };

            used.add(currentIndex);

            // Find all related URLs
            urls.forEach((checkUrl, checkIndex) => {
                if (used.has(checkIndex)) return;
                if (currentUrl.supplier !== checkUrl.supplier) return;

                // Check if URLs are related
                const url1Base = currentUrl.url.split('?')[0];
                const url2Base = checkUrl.url.split('?')[0];

                const related = url1Base === url2Base ||
                    currentUrl.url.includes(url2Base) ||
                    checkUrl.url.includes(url1Base);

                if (related) {
                    group.items.push(checkUrl);
                    used.add(checkIndex);
                }
            });

            // Sort items by URL length (shortest first, usually page 1)
            group.items.sort((a, b) => a.url.length - b.url.length);
            group.baseUrl = group.items[0].url;

            groups.push(group);
        });

        return groups;
    };

    // Minimized View
    if (isMinimized) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition">
                <div className="p-4 cursor-pointer" onClick={onToggleMinimize}>
                    <div className="flex items-center justify-between">
                        {/* Left Side - Title, Date, Status */}
                        <div className="flex items-center gap-4 flex-1">
                            <button
                                className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded"
                            >
                                <ChevronDown size={20} />
                            </button>

                            <div>
                                <h3 className="text-lg font-bold text-gray-800">
                                    {group.group_name}
                                    <span className="text-sm text-gray-600 ml-2">
                                        {new Date(group.created_at).toLocaleDateString()}
                                    </span>
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    {getStatusBadge()}
                                </div>
                            </div>
                        </div>

                        {/* Middle - Suppliers */}
                        <div className="flex items-center gap-2 mx-4">
                            <span className="text-sm text-gray-600 font-semibold">Suppliers:</span>
                            {group.selected_suppliers.map(supplier => (
                                <div key={supplier}>
                                    {getSupplierLogo(supplier) && (
                                        <img
                                            src={getSupplierLogo(supplier)}
                                            alt={supplier}
                                            className="w-5 h-5 object-contain"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4">
                            {/* Progress */}
                            <div className="text-center">
                                <div className="text-xs text-gray-600 font-semibold">Pages</div>
                                <div className="text-lg font-bold text-blue-800">
                                    {group.total_urls}
                                </div>
                            </div>

                            {/* Total Deals */}
                            <div className="text-center">
                                <div className="text-xs text-gray-600 font-semibold">Deals</div>
                                <div className="text-lg font-bold text-green-800">
                                    {group.deals_count || 0}
                                </div>
                            </div>

                            {/* View Deals Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect();
                                }}
                                className="bg-gradient-to-r from-teal-500 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
                            >
                                View Deals
                            </button>

                            {/* Stop/Resume Button */}
                            {(group.status === 'processing' || group.status === 'pending') && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onStop(group.id);
                                    }}
                                    className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition flex items-center gap-2"
                                >
                                    <XCircle size={20} />
                                    Stop
                                </button>
                            )}

                            {group.status === 'stopped' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onResume(group.id);
                                    }}
                                    className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition flex items-center gap-2"
                                >
                                    <RefreshCw size={20} />
                                    Resume
                                </button>
                            )}

                            {/* Rerun Button */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onRerun(); }}
                                disabled={isRerunning}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-1 text-sm"
                                title="Delete deals and rerun"
                            >
                                {isRerunning ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                                Rerun
                            </button>

                            {/* Delete Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            >
                                {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Expanded View (Original)
    return (
        <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2 flex-1">
                        <button
                            onClick={onToggleMinimize}
                            className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded"
                        >
                            <ChevronUp size={20} />
                        </button>

                        <div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                {group.group_name}
                                <span className="text-sm text-gray-600 ml-2">
                                    {new Date(group.created_at).toLocaleDateString()}
                                </span>
                            </h3>
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                {getStatusBadge()}
                                <span className="font-semibold text-gray-800 text-sm ml-2">
                                    Suppliers:
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {group.selected_suppliers.map(supplier => (
                                        <div key={supplier} className="flex items-center gap-2 py-1.5 rounded-lg">
                                            {getSupplierLogo(supplier) && (
                                                <img
                                                    src={getSupplierLogo(supplier)}
                                                    alt={supplier}
                                                    className="w-5 h-5 object-contain"
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stop/Resume Button */}
                    {(group.status === 'processing' || group.status === 'pending') && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onStop(group.id);
                            }}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition flex items-center gap-2"
                        >
                            <XCircle size={20} />
                            Stop
                        </button>
                    )}

                    {group.status === 'stopped' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onResume(group.id);
                            }}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition flex items-center gap-2"
                        >
                            <RefreshCw size={20} />
                            Resume
                        </button>
                    )}&emsp;

                    <button
                        onClick={(e) => { e.stopPropagation(); onRerun(); }}
                        disabled={isRerunning}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-1 text-sm"
                        title="Delete deals and rerun"
                    >
                        {isRerunning ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                        Rerun
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        disabled={isDeleting}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                    >
                        {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                    </button>
                </div>

                {/* Detailed Status Panel - Always Visible */}
                <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Package size={18} />
                        Processing Status
                    </h4>

                    {/* Status Counts */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                        <div className="text-center p-2 bg-blue-50 rounded">
                            <div className="text-xs text-blue-600 font-semibold">Pages Processing</div>
                            <div className="text-lg font-bold text-blue-800">
                                {(group.url_status_counts?.pending + group.url_status_counts?.processing) || 0}
                            </div>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded">
                            <div className="text-xs text-green-600 font-semibold">Pages Fetched <InfoTooltip text={`Page processed. Products found, now processing them to find deals!`} /></div>
                            <div className="text-lg font-bold text-green-800">
                                {group.url_status_counts?.completed || 0}
                            </div>
                        </div>
                        <div className="text-center p-2 bg-red-50 rounded">
                            <div className="text-xs text-red-600 font-semibold">Pages Failed <InfoTooltip text={`Make sure the page is a 'results' page (catalog). Contact us for quick help! (FB/Discord/Email)`} /></div>
                            <div className="text-lg font-bold text-red-800">
                                {group.url_status_counts?.failed || 0}
                            </div>
                        </div>
                        <div className="text-center p-2 bg-purple-100 rounded border border-purple-800">
                            <div className="text-xs text-purple-700 font-semibold mb-1 flex items-center gap-1 justify-center">Total Deals Found {group.status === 'processing' && <RefreshCw className="animate-spin" size={16} />}</div>
                            <div className="text-xl font-bold text-purple-700">
                                {group.deals_count || 0}
                            </div>
                        </div>
                    </div>

                    {/* URL Details List */}
                    <div className="max-h-64 overflow-y-auto space-y-2">
                        {groupRelatedUrls(group.url_details || []).map((urlGroup, groupIdx) => {
                            // Calculate totals for this group
                            const totalProducts = urlGroup.items.reduce((sum, item) => sum + (item.products_found || 0), 0);
                            const allCompleted = urlGroup.items.every(item => item.status === 'completed');
                            const anyProcessing = urlGroup.items.some(item => item.status === 'processing');
                            const anyFailed = urlGroup.items.some(item => item.status === 'failed');
                            const hasErrors = urlGroup.items.some(item => item.error_message);

                            // Determine overall status
                            let overallStatus = 'pending';
                            if (allCompleted) overallStatus = 'completed';
                            else if (anyProcessing) overallStatus = 'processing';
                            else if (anyFailed) overallStatus = 'failed';

                            return (
                                <div key={groupIdx} className="bg-white border border-gray-200 rounded p-3">
                                    {/* Header Row */}
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            {getSupplierLogo(urlGroup.supplier) && (
                                                <img
                                                    src={getSupplierLogo(urlGroup.supplier)}
                                                    alt={urlGroup.supplier}
                                                    className="w-4 h-4 object-contain"
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            )}
                                            <span className="text-sm font-semibold text-gray-800">
                                                {urlGroup.supplier}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {/* Total Products */}
                                            {totalProducts > 0 && (
                                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-semibold">
                                                    📦 {totalProducts}
                                                </span>
                                            )}

                                            {/* Pages Count */}
                                            {urlGroup.items.length > 1 && (
                                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-semibold">
                                                    {urlGroup.items.length} pages
                                                </span>
                                            )}

                                            {/* Status */}
                                            <span className={`text-xs px-2 py-1 rounded font-semibold ${overallStatus === 'completed' ? 'bg-green-100 text-green-800' :
                                                overallStatus === 'processing' ? 'bg-blue-100 text-blue-800' :
                                                    overallStatus === 'failed' ? 'bg-red-100 text-red-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {overallStatus}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Base URL */}
                                    <div className="text-xs text-gray-600 truncate mb-2">
                                        {urlGroup.baseUrl}
                                    </div>

                                    {/* Individual Pages */}
                                    {urlGroup.items.length > 1 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {urlGroup.items.map((item, itemIdx) => {
                                                return (
                                                    <a href={item.url} target="_blank" rel="noreferrer">
                                                        <span
                                                            key={itemIdx}
                                                            className={`text-xs px-2 py-1 rounded cursor-pointer ${item.error_message ? 'bg-orange-100 text-orange-700' :
                                                                item.status === 'completed' ? 'bg-green-50 text-green-700' :
                                                                    item.status === 'processing' ? 'bg-blue-50 text-blue-700 animate-pulse' :
                                                                        item.status === 'failed' ? 'bg-red-50 text-red-700' :
                                                                            'bg-yellow-50 text-yellow-700'
                                                                }`}
                                                            title={item.url}
                                                        >
                                                            Page {itemIdx + 1} {item.products_found > 0 ? `(${item.products_found})` : ''}
                                                            {item.error_message && (
                                                                <InfoTooltip text={item.error_message === 'No products found' && itemIdx === urlGroup.items.length - 1
                                                                    ? `Page ${itemIdx} is the last page`
                                                                    : item.error_message} />
                                                            )}
                                                        </span>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Single Page Products */}
                                    {urlGroup.items.length === 1 && urlGroup.items[0].error_message && (
                                        <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded">
                                            ⚠️ {urlGroup.items[0].error_message}
                                        </div>
                                    )}
                                    {urlGroup.items.length === 1 && !urlGroup.items[0].error_message && urlGroup.items[0].products_found === 0 && (
                                        <div className="text-xs text-gray-500 italic">
                                            No products found yet
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* View Results Button */}
                <button
                    onClick={onSelect}
                    className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
                >
                    <TrendingUp size={20} />
                    View Deals
                </button>
            </div>
        </div>
    );
};

const ScanGroupDetails = ({ group, onClose, user }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tempDeals, setTempDeals] = useState([]);
    const [hasNewDeals, setHasNewDeals] = useState(false);
    const [ignoredNewDeals, setIgnoredNewDeals] = useState(false);

    useEffect(() => {
        setDeals([]);
        setTempDeals([]);
        setHasNewDeals(false);
        loadDeals();

        // Auto-refresh every 20 seconds if processing
        const interval = setInterval(() => {
            if (group.status === 'processing') {
                loadDeals(true);
            }
        }, 20000);

        return () => clearInterval(interval);
    }, [group.id]);

    const loadDeals = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/live-scan/groups/${group.id}/deals`
            );
            const data = await response.json();
            if (response.ok) {
                const incoming = data.deals || [];
                setDeals(prev => {
                    if (prev.length === 0) return incoming;
                    const existingIds = new Set(prev.map(d => d.id));
                    const newOnes = incoming.filter(d => !existingIds.has(d.id));
                    const updated = prev.map(d => {
                        const match = incoming.find(i => i.id === d.id);
                        return match ? match : d;
                    });
                    if (newOnes.length > 0) {
                        setTempDeals(newOnes);
                        setHasNewDeals(true);
                    }
                    return updated;
                });
            }
        } catch (error) {
            console.error('Failed to load deals:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
            >
                ✕
            </button>
            <UpdateDealsOnboardingModal show={hasNewDeals} scanType="live" />
            {hasNewDeals && !ignoredNewDeals && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4">
                    <span>🎉 {tempDeals.length} new deal{tempDeals.length > 1 ? 's' : ''} found!</span>
                    <button
                        onClick={() => {
                            setDeals(prev => [...tempDeals, ...prev]);
                            setTempDeals([]);
                            setHasNewDeals(false);
                        }}
                        className="bg-teal-500 hover:bg-teal-400 px-3 py-1 rounded-full text-sm font-semibold"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => {
                            setIgnoredNewDeals(true);
                            setHasNewDeals(false);
                        }}
                        className="text-gray-400 hover:text-white text-sm"
                    >
                        ✕
                    </button>
                </div>
            )}
            <DealsViewer
                deals={deals}
                loading={loading}
                title={group.group_name}
                showFilters={true}
                user={user}
            />
        </div>
    );
};

export default LiveScanPage;